<template>
  <b-card>
    <div slot="header" class="d-flex justify-content-between align-items-center flex-wrap">
      <span>
        <i class='fa fa-align-justify'></i> Patients
      </span>
      <b-button variant="success" @click="showAddPatientModal">
        Add Patient
        <b-badge variant="light" class="ml-1">NEW</b-badge>
      </b-button>
    </div>

    <b-nav tabs class="patient-tabs mb-3">
      <b-nav-item :active="activeTab === 'all'" @click="setActiveTab('all')">All patients</b-nav-item>
      <b-nav-item :active="activeTab === 'invites'" @click="setActiveTab('invites')">
        Consultation invites
        <b-badge variant="warning" class="ml-1" v-if="inviteStats.invitesPending > 0">{{ inviteStats.invitesPending }}</b-badge>
      </b-nav-item>
    </b-nav>

    <div v-if="activeTab === 'invites'" class="text-right mb-2">
      <router-link :to="{ path: '/tracking/sessions', query: { entrySurface: 'ADMIN_BOOKING' } }">
        <i class="fa fa-line-chart"></i> View admin booking journeys in Tracking
      </router-link>
    </div>
    <b-row v-if="activeTab === 'invites'" class="invite-stats-row mb-3">
      <b-col sm="6" md="3">
        <div class="invite-stat-card">
          <div class="invite-stat-label">Links sent</div>
          <div class="invite-stat-value">{{ inviteStats.inviteLinksStored }}</div>
          <div class="invite-stat-help">Patients with a stored payment link</div>
        </div>
      </b-col>
      <b-col sm="6" md="3">
        <div class="invite-stat-card pending">
          <div class="invite-stat-label">Awaiting payment</div>
          <div class="invite-stat-value">{{ inviteStats.invitesPending }}</div>
          <div class="invite-stat-help">No booked consultation yet</div>
        </div>
      </b-col>
      <b-col sm="6" md="3">
        <div class="invite-stat-card ok">
          <div class="invite-stat-label">Paid / booked</div>
          <div class="invite-stat-value">{{ inviteStats.invitesPaid }}</div>
          <div class="invite-stat-help">Consultation order is booked</div>
        </div>
      </b-col>
      <b-col sm="6" md="3">
        <div class="invite-stat-card">
          <div class="invite-stat-label">Slots on hold</div>
          <div class="invite-stat-value">{{ inviteStats.slotHoldsActive }}</div>
          <div class="invite-stat-help">Active admin slot holds</div>
        </div>
      </b-col>
    </b-row>

    <b-row class="patient-filters align-items-end">
      <b-col sm="12" md="5">
        <b-pagination size="md" :total-rows="patients.totalElements" v-model="currentPage" :per-page="perPage"></b-pagination>
      </b-col>
      <b-col sm="12" md="4">
        <b-form-group label="Find a patient" class="mb-2">
          <b-input-group>
            <b-form-input
              v-model="searchQuery"
              type="search"
              placeholder="Name, email, phone, ID or postcode"
              @keyup.enter="applySearch"
            />
            <b-input-group-append>
              <b-button variant="primary" :disabled="searching" @click="applySearch">
                <b-spinner v-if="searching" small label="Searching" />
                <i v-else class="fa fa-search"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
          <small v-if="searchedFor && !searching" class="text-muted">
            {{ patients.totalElements || 0 }} {{ patients.totalElements === 1 ? 'patient' : 'patients' }} found for "{{ searchedFor }}"
          </small>
        </b-form-group>
      </b-col>
      <b-col sm="12" md="3" v-if="activeTab === 'all'">
        <b-form-group label="Payment status" class="mb-2">
          <b-form-select v-model="paymentFilter" :options="paymentFilterOptions" @change="applySearch" />
        </b-form-group>
      </b-col>
      <b-col sm="12" md="3" v-else>
        <b-form-group label="Invite status" class="mb-2">
          <b-form-select v-model="inviteFilter" :options="inviteFilterOptions" @change="applySearch" />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-table :items="patients.content" :fields="tableFields" :current-page="1" :per-page="perPage" :busy="searching" hover outlined show-empty responsive>
          <template v-slot:table-busy>
            <div class="text-center text-muted my-2"><b-spinner small class="mr-2" />Searching…</div>
          </template>
          <template v-slot:cell(externallyIdentified)="row">
            <b-form-checkbox block variant="primary" v-model="row.value" :disabled="true" />
          </template>
          <template v-slot:cell(email)="row">
            <a :href="'mailto:' + row.value" target="_blank">{{row.value}}</a>
          </template>
          <template v-slot:cell(creationDate)="row">
            <span v-if="row.value">{{ row.value | dateTime }}</span>
            <span v-else class="text-muted">—</span>
          </template>
          <template v-slot:cell(payment)="row">
            <span v-if="row.item.paymentStatus === 'PENDING' && row.item.inviteHoldState === 'RELEASED'" class="patient-pay-pill muted">Cancelled</span>
            <span v-else-if="row.item.paymentStatus === 'PENDING' && (row.item.inviteHoldState === 'EXPIRED' || isHoldTimedOut(row.item))" class="patient-pay-pill muted">Link expired</span>
            <span v-else-if="row.item.paymentStatus === 'PENDING'" class="patient-pay-pill pending">Awaiting payment</span>
            <span v-else-if="row.item.paymentStatus === 'PAID'" class="patient-pay-pill ok">Booked</span>
            <span v-else-if="activeTab === 'invites'" class="patient-pay-pill muted">Invite sent</span>
            <span v-else class="text-muted">—</span>
            <div v-if="holdCountdown(row.item)" class="small mt-1" :class="holdCountdownUrgent(row.item) ? 'text-danger' : 'text-muted'">
              {{ holdCountdown(row.item) }}
            </div>
            <div v-if="row.item.passwordSetupPending" class="mt-1">
              <span class="patient-pay-pill pending" title="The patient has not chosen a password yet. Use Action → Send set-password link.">
                Password not set
              </span>
            </div>
          </template>
          <template v-slot:cell(checkout)="row">
            <span v-if="row.item.inviteStage" class="patient-pay-pill" :class="stageInfo(row.item.inviteStage).tone"
              :title="row.item.inviteError || ''">
              {{ stageInfo(row.item.inviteStage).label }}
            </span>
            <span v-else class="text-muted">—</span>
            <div v-if="row.item.inviteStageAt" class="small text-muted mt-1">{{ row.item.inviteStageAt | dateTime }}</div>
          </template>
          <template v-slot:cell(actions)="data">
            <div class="patient-row-actions">
              <router-link
                class="btn btn-success btn-sm mr-2"
                :to="{ path: '/patient/' + data.item.id, query: { book: '1' }}"
              >
                Book Consultation
                <b-badge variant="light" class="ml-1">NEW</b-badge>
              </router-link>
              <!-- Fixed positioning so the menu is not clipped by the scrolling table when there are few rows. -->
              <b-dropdown variant="primary" size="sm" center text="Action" right :popper-opts="{ positionFixed: true }">
                <b-dropdown-item :to="{ path: '/patient/' + data.item.id}">View / Edit</b-dropdown-item>
                <b-dropdown-item :to="{ path: '/patient/' + data.item.id, query: { book: '1' }}">
                  Book Consultation
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="isLinkOpen(data.item) && data.item.paymentLinkUrl"
                  href="#"
                  @click.prevent="showPaymentLink(data.item)"
                >
                  View / copy payment link
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="isLinkOpen(data.item)"
                  href="#"
                  :disabled="!!resendingIds[data.item.id]"
                  @click.prevent="resendPaymentLink(data.item)"
                >
                  {{ resendingIds[data.item.id] ? 'Resending...' : 'Resend payment link' }}
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="isLinkOpen(data.item) && data.item.inviteHoldState === 'ACTIVE'"
                  href="#"
                  variant="danger"
                  :disabled="!!resendingIds[data.item.id]"
                  @click.prevent="cancelPaymentLink(data.item)"
                >
                  Cancel payment link
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="data.item.email"
                  href="#"
                  :disabled="!!passwordLinkIds[data.item.id]"
                  @click.prevent="sendPasswordLink(data.item)"
                >
                  {{ passwordLinkLabel(data.item) }}
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="activeTab === 'invites' && data.item.email"
                  :to="{ path: '/tracking/sessions', query: { entrySurface: 'ADMIN_BOOKING', email: data.item.email } }"
                >
                  View tracking journey
                </b-dropdown-item>
                <b-dropdown-item :to="{ path: '/change-password/patient/' + data.item.id}">Change Password</b-dropdown-item>
              </b-dropdown>
            </div>
          </template>
        </b-table>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-pagination size="md" :total-rows="patients.totalElements" v-model="currentPage" :per-page="perPage"></b-pagination>
      </b-col>
    </b-row>

    <br>

    <b-modal ref="add-patient-modal" hide-footer size="lg" @hide="resetAddPatientForm" @shown="focusFirstField">
      <template slot="modal-title">
        Add Patient
        <b-badge variant="success" class="ml-2">NEW</b-badge>
      </template>
      <p class="add-patient-help">
        Create the patient here, then you can book a consultation or send a payment link.
        Fields marked <span class="text-danger">*</span> are required.
      </p>
      <b-form @submit.prevent="submitAddPatient" novalidate>
        <b-row>
          <b-col md="3">
            <b-form-group label="Title">
              <b-form-select v-model="newPatient.title" :options="titleOptions" @change="onTitleChange" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group :state="fieldState('firstName')" :invalid-feedback="fieldErrors.firstName">
              <template slot="label">First name <span class="text-danger">*</span></template>
              <b-form-input ref="firstField" v-model="newPatient.firstName" :state="fieldState('firstName')" autocomplete="off" />
            </b-form-group>
          </b-col>
          <b-col md="5">
            <b-form-group :state="fieldState('lastName')" :invalid-feedback="fieldErrors.lastName">
              <template slot="label">Last name <span class="text-danger">*</span></template>
              <b-form-input v-model="newPatient.lastName" :state="fieldState('lastName')" autocomplete="off" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group :state="fieldState('email')" :invalid-feedback="fieldErrors.email">
              <template slot="label">Email <span class="text-danger">*</span></template>
              <b-form-input v-model="newPatient.email" type="email" placeholder="name@example.com" :state="fieldState('email')" autocomplete="off" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group :state="fieldState('phone')" :invalid-feedback="fieldErrors.phone">
              <template slot="label">Phone <span class="text-danger">*</span></template>
              <b-form-input v-model="newPatient.phone" type="tel" placeholder="07123 456789" :state="fieldState('phone')" autocomplete="off" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group :state="fieldState('dateOfBirth')" :invalid-feedback="fieldErrors.dateOfBirth" description="Type it, e.g. 14/03/1985">
              <template slot="label">Date of birth <span class="text-danger">*</span></template>
              <b-form-input v-model="newPatient.dateOfBirth" type="date" :max="maxDateOfBirth" min="1900-01-01" :state="fieldState('dateOfBirth')" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Gender" :description="genderTouched ? '' : 'Set from title — change if needed'">
              <b-form-radio-group v-model="newPatient.gender" :options="genderOptions" buttons button-variant="outline-primary" @change="genderTouched = true" />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="add-patient-address">
          <b-form-group :state="fieldState('postcode')" :invalid-feedback="fieldErrors.postcode" class="mb-2">
            <template slot="label">Postcode <span class="text-danger">*</span></template>
            <b-input-group>
              <b-form-input v-model="newPatient.postcode" placeholder="e.g. SW1A 1AA" :state="fieldState('postcode')" autocomplete="off" @keydown.enter.prevent="findAddresses" />
              <b-input-group-append>
                <b-button variant="outline-primary" :disabled="addressLookup.loading || !newPatient.postcode" @click="findAddresses">
                  <b-spinner small v-if="addressLookup.loading" /> Find address
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
          <b-form-group v-if="addressLookup.options.length" label="Pick the address" class="mb-2">
            <b-form-select v-model="addressLookup.selected" :options="addressLookup.options" @change="applyAddress">
              <template slot="first"><option :value="null">{{ addressLookup.options.length }} addresses found — choose one</option></template>
            </b-form-select>
          </b-form-group>
          <p v-if="addressLookup.message" class="add-patient-lookup-msg">{{ addressLookup.message }}</p>

          <b-form-group :state="fieldState('line1')" :invalid-feedback="fieldErrors.line1" class="mb-2">
            <template slot="label">Address line 1 <span class="text-danger">*</span></template>
            <b-form-input v-model="newPatient.line1" placeholder="House number and street" :state="fieldState('line1')" />
          </b-form-group>
          <b-form-group label="Address line 2" class="mb-2">
            <b-form-input v-model="newPatient.line2" />
          </b-form-group>
          <b-row>
            <b-col md="6">
              <b-form-group :state="fieldState('city')" :invalid-feedback="fieldErrors.city">
                <template slot="label">Town / city <span class="text-danger">*</span></template>
                <b-form-input v-model="newPatient.city" :state="fieldState('city')" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="County">
                <b-form-input v-model="newPatient.county" />
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <b-alert :show="!!addPatientError" variant="danger">
          {{ addPatientError }}
          <div v-if="existingPatient" class="mt-2">
            <b-button size="sm" variant="primary" :to="'/patient/' + existingPatient.id">
              Open {{ existingPatient.displayName || existingPatient.email }}
            </b-button>
            <b-button size="sm" variant="outline-primary" class="ml-1" :to="{ path: '/patient/' + existingPatient.id, query: { book: '1' } }">
              Book for this patient
            </b-button>
          </div>
        </b-alert>
        <b-button type="submit" variant="success" block :disabled="isCreatingPatient">
          {{ isCreatingPatient ? 'Creating...' : 'Create patient' }}
        </b-button>
      </b-form>
    </b-modal>

    <payment-link-modal ref="paymentLinkModal" @show-invites="setActiveTab('invites')" />
  </b-card>
</template>

<script>

import moment from 'moment';
import PaymentLinkModal from '../../components/PaymentLinkModal';
import publicService from '../../services/PublicService';
import { parseAddressLine, addressLabel, looksLikeUkPostcode, looksLikeUkPhone } from '../../utils/ukAddress';

const FEMALE_TITLES = ['Mrs', 'Miss', 'Ms'];

// Payment-link checkout progress (admin_booking_lead.status).
const INVITE_STAGES = {
  SENT: { label: 'Link sent', tone: 'muted' },
  OPENED: { label: 'Link opened', tone: 'muted' },
  PAYMENT_FAILED: { label: 'Payment failed', tone: 'failed' },
  PAID: { label: 'Paid', tone: 'pending' },
  OTP_SENT: { label: 'Paid · OTP sent', tone: 'pending' },
  VERIFIED: { label: 'Paid · Verified · password not set', tone: 'pending' },
  PASSWORD_SET: { label: 'Paid · Account ready', tone: 'ok' },
  CANCELLED: { label: 'Cancelled', tone: 'muted' }
};

function emptyAddressLookup () {
  return { loading: false, options: [], selected: null, message: '' };
}

function emptyNewPatient () {
  return {
    title: 'Mr',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: 'MALE',
    line1: '',
    line2: '',
    city: '',
    county: '',
    postcode: ''
  };
}

export default {
  components: { PaymentLinkModal },
  data: function () {
    return {
      searchQuery: '',
      searching: false,
      searchedFor: '',
      searchSeq: 0,
      selectedQuery: '',
      emailSearchQuery: '',
      selectedEmailQuery: '',
      activeTab: 'all',
      paymentFilter: 'all',
      inviteFilter: 'invites',
      inviteStats: {
        inviteLinksStored: 0,
        invitesPending: 0,
        invitesPaid: 0,
        slotHoldsActive: 0,
        slotHoldsUsed: 0,
        adminInstantBookings: 0
      },
      paymentFilterOptions: [
        { text: 'All patients', value: 'all' },
        { text: 'Pending payment link', value: 'pending' },
        { text: 'Paid via link', value: 'paid' }
      ],
      inviteFilterOptions: [
        { text: 'All invites', value: 'invites' },
        { text: 'Awaiting payment', value: 'pending' },
        { text: 'Booked', value: 'paid' }
      ],
      resendingIds: {},
      patients: [],
      columnNames: [
        'id',
        { key: 'externallyIdentified', 'class': 'text-center' },
        'gender',
        'displayName',
        'email',
        'phone',
        'dateOfBirth',
        { key: 'creationDate', label: 'Registered' },
        { key: 'payment', label: 'Payment' },
        'actions'
      ],
      currentPage: 1,
      perPage: 10,
      patientsList: [],
      patientEmailList: [],
      selectedAddress: null,
      isCreatingPatient: false,
      passwordLinkIds: {},
      addPatientError: '',
      addPatientSubmitted: false,
      fieldErrors: {},
      genderTouched: false,
      existingPatient: null,
      addressLookup: emptyAddressLookup(),
      newPatient: emptyNewPatient(),
      titleOptions: [
        { text: 'Mr', value: 'Mr' },
        { text: 'Mrs', value: 'Mrs' },
        { text: 'Miss', value: 'Miss' },
        { text: 'Ms', value: 'Ms' },
        { text: 'Dr', value: 'Dr' },
        { text: 'Prof', value: 'Prof' }
      ],
      genderOptions: [
        { text: 'Male', value: 'MALE' },
        { text: 'Female', value: 'FEMALE' }
      ],
      /** Bumps every second so hold countdowns re-render. */
      nowTick: Date.now(),
      holdTickTimer: null
    }
  },
  computed: {
    maxDateOfBirth () {
      return moment().subtract(18, 'years').format('YYYY-MM-DD');
    },
    tableFields () {
      if (this.activeTab !== 'invites') {
        return this.columnNames;
      }
      // Invites only need who, contact, status and progress; keep the table narrow enough to fit.
      const hidden = ['externallyIdentified', 'gender', 'dateOfBirth'];
      const fields = this.columnNames.filter(f => hidden.indexOf(typeof f === 'string' ? f : f.key) < 0);
      fields.splice(fields.length - 1, 0, { key: 'checkout', label: 'Checkout progress' });
      return fields;
    }
  },
  filters: {
			dateTime: function (date) {
				return moment(date).local().format('HH:mm - DD/MM/YY');
      }
  },
  created () {
    // From the call-back queue: open Add patient with first name, mobile and email filled in.
    if (this.$route.query.add === '1') {
      let prefill = null;
      try {
        prefill = JSON.parse(sessionStorage.getItem('callbackPrefill') || 'null');
        sessionStorage.removeItem('callbackPrefill');
      } catch (e) { prefill = null; }
      this.$nextTick(() => {
        this.showAddPatientModal();
        if (prefill) {
          Object.assign(this.newPatient, {
            firstName: prefill.firstName || '',
            phone: prefill.phone || '',
            email: prefill.email || ''
          });
        }
      });
    }
    if (sessionStorage.getItem('patientsActiveTab') === 'invites') {
      sessionStorage.removeItem('patientsActiveTab');
      this.activeTab = 'invites';
    }
    this.search();
    this.loadInviteStats();
    this.holdTickTimer = setInterval(() => {
      this.nowTick = Date.now();
    }, 1000);
  },
  beforeDestroy () {
    if (this.holdTickTimer) {
      clearInterval(this.holdTickTimer);
      this.holdTickTimer = null;
    }
  },
  watch: {
    currentPage: function () {
      this.search();
    }
  },
  methods: {
    applySearch () {
      if (this.currentPage !== 1) {
        this.currentPage = 1;
        return;
      }
      this.search();
    },
    setActiveTab (tab) {
      if (this.activeTab === tab) {
        return;
      }
      this.activeTab = tab;
      if (tab === 'invites') {
        this.loadInviteStats();
      }
      this.applySearch();
    },
    loadInviteStats () {
      this.$patientService.getAdminBookingSummary()
        .then(data => {
          if (data) {
            this.inviteStats = Object.assign({}, this.inviteStats, data);
          }
        })
        .catch(() => {});
    },
    search () {
      const q = (this.searchQuery || '').trim();
      const payment = this.activeTab === 'invites' ? this.inviteFilter : this.paymentFilter;
      // Show progress (the search takes ~1 s) and keep only the newest answer when clicked repeatedly.
      const seq = ++this.searchSeq;
      this.searching = true;
      this.$patientService.search(q, this.currentPage - 1, this.perPage, {
        payment: payment
      }).then(patients => {
        if (seq !== this.searchSeq) {
          return;
        }
        patients.content = patients.content.map((patient) => {
          patient.dateOfBirth = patient.dateOfBirth ? moment(patient.dateOfBirth).format('DD MMM YYYY') : '';
          return patient;
        });
        this.patients = patients;
        this.searchedFor = q;
      }).catch(error => {
        if (seq === this.searchSeq) {
          this.$awn.alert(error, 'An error occurred');
        }
      }).finally(() => {
        if (seq === this.searchSeq) {
          this.searching = false;
        }
      });
    },
    resendPaymentLink (patient) {
      if (!patient || !patient.paymentLinkUrl) {
        this.$awn.alert('No payment link was stored for this patient.');
        return;
      }
      if (!patient.email) {
        this.$awn.alert('This patient does not have an email address.');
        return;
      }
      const patientName = [patient.firstName, patient.lastName].filter(Boolean).join(' ');
      this.$set(this.resendingIds, patient.id, true);
      // Consultation fee from Pricing (GPS); £39 if it can't be loaded.
      this.$pricingService.gpsPrice('appointmentPrice', 39.00).then(fee => this.$orderService.resendDocumentConsent({
        message: 'Our team has reserved a GP video consultation for you. Please pay within 10 minutes using the link below to confirm your booking. After that the link expires and the slot is released.',
        emailTemplateName: 'prescription-order-summary-payment.html',
        whatsAppTemplate: '',
        subject: 'Complete payment to confirm your GP video consultation',
        isSmsEnable: false,
        isWhatsAppEnable: false,
        isEmailEnable: true,
        type: 'DOCUMENT-PAYMENT-REQUEST',
        heading: 'Complete payment for your GP video consultation',
        url1: patient.paymentLinkUrl,
        email: [patient.email],
        mobile: patient.phone ? [patient.phone] : [],
        userName: patientName,
        patientNameForInvite: patientName,
        documentType: 'consultation',
        additionalServiceFee: fee,
        prescriptionFee: fee,
        totalCharge: fee,
        notificationCat: 'Consultation payment link'
      })).then(res => {
        if (res && res.status === 'OK') {
          this.$awn.success('Payment link resent to ' + patient.email);
        } else {
          this.$awn.alert((res && res.message) || 'Failed to resend the payment link.');
        }
      }).catch(error => {
        const message = error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : (error && error.message) || 'Failed to resend the payment link.';
        this.$awn.alert(message);
      }).finally(() => {
        this.$delete(this.resendingIds, patient.id);
      });
    },
    passwordLinkLabel (patient) {
      if (this.passwordLinkIds[patient.id]) {
        return 'Sending...';
      }
      return patient.passwordSetupPending ? 'Send set-password link' : 'Send password reset link';
    },
    sendPasswordLink (patient) {
      this.$set(this.passwordLinkIds, patient.id, true);
      this.$patientService.sendSetPasswordLink(patient.id).then(res => {
        const data = (res && res.data) || {};
        this.$awn.success((data.type === 'RESET_PASSWORD' ? 'Password reset link' : 'Set-password link')
          + ' sent to ' + (data.email || patient.email));
      }).catch(error => {
        const message = error && error.response && error.response.data && error.response.data.message;
        this.$awn.alert(message || 'Could not send the password link.');
      }).finally(() => {
        this.$delete(this.passwordLinkIds, patient.id);
      });
    },
    showPaymentLink (patient) {
      this.$refs.paymentLinkModal.show({
        patientId: patient.id,
        name: [patient.firstName, patient.lastName].filter(Boolean).join(' '),
        email: patient.email,
        link: patient.paymentLinkUrl,
        expiresAt: patient.inviteHoldExpiresAt || null
      });
    },
    isLinkOpen (patient) {
      return patient.paymentStatus === 'PENDING'
        && patient.inviteHoldState !== 'RELEASED'
        && patient.inviteHoldState !== 'EXPIRED'
        && !this.isHoldTimedOut(patient);
    },
    isHoldTimedOut (patient) {
      if (!patient || !patient.inviteHoldExpiresAt || patient.inviteHoldState !== 'ACTIVE') {
        return false;
      }
      void this.nowTick;
      return moment(patient.inviteHoldExpiresAt).isSameOrBefore(moment());
    },
    holdCountdown (patient) {
      void this.nowTick;
      if (!patient || patient.paymentStatus !== 'PENDING' || !patient.inviteHoldExpiresAt) {
        return '';
      }
      if (patient.inviteHoldState === 'RELEASED' || patient.inviteHoldState === 'EXPIRED' || this.isHoldTimedOut(patient)) {
        return '';
      }
      if (patient.inviteHoldState !== 'ACTIVE') {
        return '';
      }
      const ms = moment(patient.inviteHoldExpiresAt).diff(moment());
      if (ms <= 0) {
        return '';
      }
      const total = Math.ceil(ms / 1000);
      const m = Math.floor(total / 60);
      const s = total % 60;
      return `Hold ends in ${m}:${s < 10 ? '0' : ''}${s}`;
    },
    holdCountdownUrgent (patient) {
      void this.nowTick;
      if (!patient || !patient.inviteHoldExpiresAt || patient.inviteHoldState !== 'ACTIVE') {
        return false;
      }
      return moment(patient.inviteHoldExpiresAt).diff(moment()) <= 60000;
    },
    stageInfo (stage) {
      return INVITE_STAGES[stage] || { label: stage, tone: 'muted' };
    },
    cancelPaymentLink (patient) {
      const text = 'Cancel the payment link for ' + (patient.displayName || patient.email)
        + '? The held slot goes back on sale and the link stops working.';
      this.$bvModal.msgBoxConfirm(text, { title: 'Cancel payment link', okVariant: 'danger', okTitle: 'Cancel link', cancelTitle: 'Keep' })
        .then(ok => {
          if (!ok) return;
          this.$set(this.resendingIds, patient.id, true);
          return this.$patientService.cancelSlotHold(patient.id).then(() => {
            this.$awn.success('Payment link cancelled');
            this.search();
            this.loadInviteStats();
          });
        })
        .catch(error => {
          const message = error && error.response && error.response.data && error.response.data.message;
          this.$awn.alert(message || 'Could not cancel the payment link.');
        })
        .finally(() => {
          this.$delete(this.resendingIds, patient.id);
        });
    },
    showAddPatientModal () {
      this.resetAddPatientForm();
      this.$refs['add-patient-modal'].show();
    },
    resetAddPatientForm () {
      this.addPatientError = '';
      this.addPatientSubmitted = false;
      this.fieldErrors = {};
      this.genderTouched = false;
      this.existingPatient = null;
      this.addressLookup = emptyAddressLookup();
      this.isCreatingPatient = false;
      this.newPatient = emptyNewPatient();
    },
    focusFirstField () {
      const input = this.$refs.firstField;
      if (input && input.focus) input.focus();
    },
    fieldState (key) {
      if (!this.addPatientSubmitted) return null;
      return this.fieldErrors[key] ? false : null;
    },
    onTitleChange (title) {
      if (this.genderTouched) return;
      if (FEMALE_TITLES.indexOf(title) >= 0) this.newPatient.gender = 'FEMALE';
      else if (title === 'Mr') this.newPatient.gender = 'MALE';
    },
    findAddresses () {
      const postcode = (this.newPatient.postcode || '').trim();
      this.addressLookup = emptyAddressLookup();
      if (!looksLikeUkPostcode(postcode)) {
        this.addressLookup.message = 'That doesn\'t look like a UK postcode — check it, or type the address below.';
        return;
      }
      this.addressLookup.loading = true;
      publicService.getRawAddressesByPostcode(postcode).then(lines => {
        this.addressLookup.options = lines.map((line, index) => {
          const fields = parseAddressLine(line);
          return { value: index, text: addressLabel(fields), fields };
        });
        if (!lines.length) {
          this.addressLookup.message = 'No addresses found for this postcode — type the address below.';
        }
      }).catch(() => {
        this.addressLookup.message = 'Address lookup is unavailable right now — type the address below.';
      }).finally(() => {
        this.addressLookup.loading = false;
      });
    },
    applyAddress (index) {
      const option = this.addressLookup.options.find(o => o.value === index);
      if (!option) return;
      Object.assign(this.newPatient, {
        line1: option.fields.line1,
        line2: option.fields.line2,
        city: option.fields.city,
        county: option.fields.county,
        postcode: this.newPatient.postcode.trim().toUpperCase()
      });
    },
    validateNewPatient () {
      const p = this.newPatient;
      const blank = (v) => !(v || '').trim();
      const errors = {};
      if (blank(p.firstName)) errors.firstName = 'Enter a first name';
      if (blank(p.lastName)) errors.lastName = 'Enter a last name';
      if (blank(p.email)) errors.email = 'Enter an email address';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email.trim())) errors.email = 'Enter a valid email address';
      if (blank(p.phone)) errors.phone = 'Enter a phone number';
      else if (!looksLikeUkPhone(p.phone)) errors.phone = 'Enter a UK number, e.g. 07123 456789';
      if (!p.dateOfBirth) errors.dateOfBirth = 'Enter a date of birth';
      else if (p.dateOfBirth > this.maxDateOfBirth) errors.dateOfBirth = 'The patient must be at least 18 years old';
      else if (p.dateOfBirth < '1900-01-01') errors.dateOfBirth = 'Check the year';
      if (blank(p.postcode)) errors.postcode = 'Enter a postcode';
      else if (!looksLikeUkPostcode(p.postcode)) errors.postcode = 'Enter a valid UK postcode';
      if (blank(p.line1)) errors.line1 = 'Enter address line 1, or use Find address';
      if (blank(p.city)) errors.city = 'Enter a town or city';
      return errors;
    },
    lookUpExistingPatient (message) {
      // The API says the email or phone is taken; find that record so the admin can jump straight to it.
      const byPhone = /phone/i.test(message || '');
      const query = byPhone ? this.newPatient.phone.trim() : this.newPatient.email.trim();
      this.$patientService.search(query, 0, 1, { email: !byPhone }).then(result => {
        const match = result && result.content && result.content[0];
        if (match) this.existingPatient = match;
      }).catch(() => {});
    },
    submitAddPatient () {
      this.addPatientError = '';
      this.existingPatient = null;
      this.addPatientSubmitted = true;
      this.fieldErrors = this.validateNewPatient();
      const problems = Object.keys(this.fieldErrors).length;
      if (problems) {
        this.addPatientError = problems === 1
          ? 'One field needs attention.'
          : problems + ' fields need attention.';
        return;
      }
      const payload = this.newPatient;
      this.isCreatingPatient = true;
      this.$patientService.createPatient({
        title: payload.title,
        firstName: payload.firstName.trim(),
        lastName: payload.lastName.trim(),
        email: payload.email.trim().toLowerCase(),
        phone: payload.phone.trim(),
        dateOfBirth: payload.dateOfBirth,
        gender: payload.gender,
        line1: payload.line1.trim(),
        line2: (payload.line2 || '').trim() || null,
        city: payload.city.trim(),
        county: (payload.county || '').trim() || null,
        postcode: payload.postcode.trim().toUpperCase()
      }).then(result => {
        const patientId = result && result.data && result.data.patientId;
        if (result && result.status === 'success' && patientId) {
          this.$refs['add-patient-modal'].hide();
          this.$awn.success('Patient created. Book a consultation now: the patient sets their password after paying.');
          this.$router.push({ path: '/patient/' + patientId, query: { book: '1' } });
          return;
        }
        this.addPatientError = (result && result.message) || 'Could not create the patient.';
      }).catch(error => {
        const data = error && error.response && error.response.data;
        let message = 'Could not create the patient.';
        if (typeof data === 'string' && data.trim()) {
          message = data;
        } else if (Array.isArray(data) && data.length) {
          message = data[0];
        } else if (data && data.message) {
          message = Array.isArray(data.message) ? data.message[0] : data.message;
        } else if (error && error.message) {
          message = error.message;
        }
        this.addPatientError = message;
        if (error && error.response && error.response.status === 409) {
          this.lookUpExistingPatient(message);
        }
      }).finally(() => {
        this.isCreatingPatient = false;
      });
    }
  }
}
</script>

<style scoped>
.patient-row-actions {
  display: flex;
  align-items: center;
  white-space: nowrap;
}
.add-patient-address {
  border-top: 1px solid #e4e7ea;
  padding-top: 12px;
  margin-bottom: 12px;
}
.add-patient-lookup-msg {
  font-size: 0.85rem;
  color: #8a5a10;
  margin-bottom: 8px;
}
.add-patient-help {
  color: #4a4a4a;
  margin-bottom: 16px;
}
.patient-pay-pill {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}
.patient-pay-pill.pending {
  background: #fff3df;
  color: #8a5a10;
}
.patient-pay-pill.ok {
  background: #e7f7ee;
  color: #176b3a;
}
.patient-pay-pill.failed {
  background: #fdecea;
  color: #a12a1f;
}
.patient-pay-pill.muted {
  background: #eef1f4;
  color: #4a5568;
}
.patient-tabs .nav-link {
  cursor: pointer;
}
/* The global .nav-link:hover (sidebar) paints tabs dark slate with unreadable text. */
.patient-tabs ::v-deep .nav-link {
  color: #4a5568;
}
.patient-tabs ::v-deep .nav-link:hover:not(.active) {
  background: #f0f3f5 !important;
  color: #23282c;
  border-color: #e4e7ea #e4e7ea #c8ced3;
}
.patient-tabs ::v-deep .nav-link.active,
.patient-tabs ::v-deep .nav-link.active:hover {
  background: #fff !important;
  color: #23282c;
  font-weight: 600;
}
.invite-stat-card {
  border: 1px solid #e4e7ea;
  border-radius: 8px;
  padding: 12px 14px;
  background: #fff;
  height: 100%;
}
.invite-stat-card.pending {
  border-color: #f0d9a8;
  background: #fffbf3;
}
.invite-stat-card.ok {
  border-color: #b8e6c8;
  background: #f6fffa;
}
.invite-stat-label {
  font-size: 0.8rem;
  color: #636b75;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.invite-stat-value {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.2;
  margin: 4px 0;
}
.invite-stat-help {
  font-size: 0.78rem;
  color: #7a828c;
}
</style>